(function () {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function videoFor(button) {
    const media = button.closest('.pts-stories__media');
    return media ? media.querySelector('video') : null;
  }

  function bind(root) {
    root.querySelectorAll('[data-pts-stories-mute]').forEach((button) => {
      if (button.dataset.ptsStoriesBound) return;
      button.dataset.ptsStoriesBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const video = videoFor(button);
        if (!video) return;

        const nextMuted = !video.muted;
        video.muted = nextMuted;
        button.setAttribute('aria-pressed', String(nextMuted));

        // Only one story may play sound at a time.
        if (!nextMuted) {
          document.querySelectorAll('.pts-stories__media video').forEach((other) => {
            if (other === video) return;
            other.muted = true;
            const otherButton = other.closest('.pts-stories__media').querySelector('[data-pts-stories-mute]');
            if (otherButton) otherButton.setAttribute('aria-pressed', 'true');
          });
          const playback = video.play();
          if (playback && typeof playback.catch === 'function') playback.catch(() => {});
        }
      });
    });

    if (REDUCED.matches) {
      root.querySelectorAll('.pts-stories__media video[autoplay]').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }

    bindCarousel(root);
  }

  // ---- carousel -------------------------------------------------------
  // The track is a scroll-snap row padded by half its leftover width, so
  // any card can sit dead centre. Whichever card is centred is the
  // emphasised one, and the row loops.
  //
  // Looping recycles the existing nodes rather than cloning: a clone
  // would duplicate every <video> in the section. When the centred card
  // runs out of neighbours on one side, the card at the far end is moved
  // round and scrollLeft is adjusted by its width, so nothing appears to
  // move.
  //
  // Emphasis is applied at rest, never mid-scroll. The featured card is
  // wider and taller, so changing it reflows the row — doing that while
  // the user is still scrolling fights their gesture.

  // ---- carousel -------------------------------------------------------
  // Scrolling, snapping, one-card-per-swipe and the easing are all CSS:
  // scroll-snap-type, scroll-snap-stop: always and scroll-behavior on the
  // track, and a transition on the card. The script only does the three
  // things CSS cannot: name the centred card, play its video, and move a
  // card round the end so the row never runs out.

  const EDGE = 2; // cards kept either side of the centred one

  const slidesOf = (track) => Array.from(track.children);

  function activeIndex(track) {
    const middle = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let closest = Infinity;
    slidesOf(track).forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - middle);
      if (distance < closest) {
        closest = distance;
        best = i;
      }
    });
    return best;
  }

  // The track declares scroll-behavior: smooth so the arrows glide. These two
  // moves have to land at once instead, and neither the scrollTo option nor
  // assigning scrollLeft reliably escapes that declaration.
  function jumpTo(track, left) {
    const declared = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = Math.max(0, left);
    track.style.scrollBehavior = declared;
  }

  function centreOn(track, slide) {
    const bounds = track.getBoundingClientRect();
    const card = slide.getBoundingClientRect();
    const drift = card.left + card.width / 2 - (bounds.left + bounds.width / 2);
    jumpTo(track, track.scrollLeft + drift);
  }

  function emphasise(track, slide) {
    const wantsPlayback = track.dataset.ptsStoriesAutoplay === 'true' && !REDUCED.matches;

    slidesOf(track).forEach((card) => {
      const centred = card === slide;
      card.classList.toggle('pts-stories__slide--featured', centred);

      const video = card.querySelector('video');
      if (!video) return;

      if (centred && wantsPlayback) {
        const playback = video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(() => {});
        return;
      }

      video.pause();
      if (centred) return;

      // a story leaving the middle gives up its sound as well as its turn
      video.muted = true;
      const toggle = card.querySelector('[data-pts-stories-mute]');
      if (toggle) toggle.setAttribute('aria-pressed', 'true');
    });
  }

  // Move a card round the end so the centred one always has neighbours. The
  // existing nodes are reused rather than cloned, which would duplicate every
  // video in the section.
  function recycle(track) {
    const count = slidesOf(track).length;
    if (track.dataset.ptsStoriesLoop !== 'true' || count < EDGE * 2 + 1) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    let guard = count;

    while (activeIndex(track) < EDGE && guard-- > 0) {
      const last = track.lastElementChild;
      const shift = last.offsetWidth + gap;
      track.prepend(last);
      jumpTo(track, track.scrollLeft + shift);
    }

    guard = count;
    while (activeIndex(track) > slidesOf(track).length - 1 - EDGE && guard-- > 0) {
      const first = track.firstElementChild;
      const shift = first.offsetWidth + gap;
      track.append(first);
      jumpTo(track, track.scrollLeft - shift);
    }
  }

  // Three lines rather than a pair of settings: the section is as tall as its
  // featured card, and mid transition the outgoing card has shrunk while the
  // incoming one has not finished growing — so without a floor the whole page
  // below jumps. Measured at rest, and it only ever rises, so changing the
  // copy cannot leave it wrong.
  function holdHeight(track) {
    const measured = track.getBoundingClientRect().height;
    if (measured > (parseFloat(track.style.minHeight) || 0)) {
      track.style.minHeight = measured + 'px';
    }
  }

  function settle(root, track, preferred) {
    holdHeight(track); // the row is at rest whenever a settle begins

    // Snapping and reordering pull against each other, so it is suspended
    // while cards are moved. Clearing the inline value hands it back to the
    // stylesheet — putting back a value read here would capture 'none' if a
    // settle began before the last one finished.
    track.style.scrollSnapType = 'none';

    if (preferred) centreOn(track, preferred);
    recycle(track);

    const slide = preferred || slidesOf(track)[activeIndex(track)];
    if (slide) {
      emphasise(track, slide);
      centreOn(track, slide);
    }

    track.style.scrollSnapType = '';
  }

  function bindCarousel(root) {
    const track = root.querySelector('.pts-stories__track');
    if (!track || track.dataset.ptsStoriesCarousel) return;
    track.dataset.ptsStoriesCarousel = 'true';

    // Mandatory snap resolves asynchronously, so the opening card is named
    // rather than read back from the scroll position.
    settle(root, track, track.querySelector('[data-pts-stories-featured]') || track.firstElementChild);

    track.addEventListener('scroll', () => {
      clearTimeout(track.ptsSettle);
      track.ptsSettle = setTimeout(() => settle(root, track), 140);
    }, { passive: true });

    window.addEventListener('resize', () => {
      track.style.minHeight = ''; // every dimension changed; measure again
      clearTimeout(track.ptsSettle);
      track.ptsSettle = setTimeout(() => settle(root, track), 200);
    }, { passive: true });

    const step = (direction) => {
      const cards = slidesOf(track);
      const target = cards[activeIndex(track) + direction];
      if (!target) return;
      // Scrolls the track and nothing else. scrollIntoView walks up every
      // scrollable ancestor, so an arrow press would also pull the page.
      const left = Math.max(0, target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2);
      track.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    };

    root.querySelector('[data-pts-stories-prev]')?.addEventListener('click', () => step(-1));
    root.querySelector('[data-pts-stories-next]')?.addEventListener('click', () => step(1));
  }

  function init() {
    document.querySelectorAll('.pts-stories').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.pts-stories');
    if (section) bind(section);
  });
})();
